import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CatalogService } from '@proxy/catalogs/catalog.service';
import { WineDto } from '@proxy/wines/models';
import { ShoppingCartService } from '@proxy/shopping-carts';
import { MetroToastService } from '../../shared/services/metro-toast.service';
import { APP_ICONS } from '../../shared/icons/app-icons';
import { StorageService } from '../../shared/services/storage.service';
import { finalize, Subject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-assisted-catalog',
  templateUrl: './assisted-catalog.component.html',
  standalone: false
})
export class AssistedCatalogComponent implements OnInit, OnDestroy {
  @Input() userId!: string;
  @Input() sommelierSlug!: string;
  @Input() clientName?: string;
  @Output() closed = new EventEmitter<void>();

  wines: WineDto[] = [];
  loading = false;
  skipCount = 0;
  maxResultCount = 20;
  totalCount = 0;
  adding: { [wineId: number]: boolean } = {};
  
  searchTerm = '';
  private searchSubject = new Subject<string>();

  selectedWineId: number | null = null;
  showDetails = false;
  showSuccess = false;
  
  iconClose = APP_ICONS.close;
  iconCart = APP_ICONS.cart;

  constructor(
    private catalogService: CatalogService,
    private shoppingCartService: ShoppingCartService,
    private toasterService: MetroToastService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.loadWines();
    
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      this.searchTerm = value;
      this.resetAndLoad();
    });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  loadWines(append: boolean = false): void {
    if (this.loading) return;
    if (append && this.wines.length >= this.totalCount && this.totalCount > 0) return;

    this.loading = true;
    
    if (!append) {
      this.skipCount = 0;
    }

    this.catalogService.getWinesPaged({
      maxResultCount: this.maxResultCount,
      skipCount: this.skipCount,
      sommelierSlug: this.sommelierSlug,
      keyword: this.searchTerm
    }).pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (result) => {
          const newWines = result.items || [];
          if (append) {
            this.wines = [...this.wines, ...newWines];
          } else {
            this.wines = newWines;
          }
          this.totalCount = result.totalCount || 0;
          this.skipCount += this.maxResultCount;
          this.loadThumbnails(newWines);
        },
        error: () => this.toasterService.error('::ErrorLoadingCatalog')
      });
  }

  resetAndLoad(): void {
    this.skipCount = 0;
    this.totalCount = 0;
    this.wines = [];
    this.loadWines();
  }

  onScroll(event: any): void {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 100) {
      this.loadWines(true);
    }
  }

  loadThumbnails(newWines: WineDto[]): void {
    newWines.forEach(wine => {
      if (wine.fileAttachmentId) {
        this.storageService.getImage(wine.fileAttachmentId, 100, 100).subscribe(blob => {
          (wine as any).imageUrl = URL.createObjectURL(blob);
        });
      }
    });
  }

  addToCart(wine: WineDto): void {
    if (this.adding[wine.id]) return;
    
    this.adding[wine.id] = true;
    this.shoppingCartService.addItem(this.userId, wine.id, 1).subscribe({
      next: () => {
        this.adding[wine.id] = false;
        this.showSuccess = true;
        setTimeout(() => this.showSuccess = false, 3000);
      },
      error: () => {
        this.adding[wine.id] = false;
        this.toasterService.error('::ErrorAddingItem');
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  openDetails(wineId: number): void {
    this.selectedWineId = wineId;
    this.showDetails = true;
  }

  closeDetails(): void {
    this.showDetails = false;
    this.selectedWineId = null;
  }

  get filteredWines(): WineDto[] {
    return this.wines;
  }

  getWineDisplayData(wine: WineDto) {
    // Si tiene maxOrderQuantity definido, usarlo; si no, usar el stock o un valor por defecto
    const maxOrder = wine.maxOrderQuantity
      ? Math.min(wine.stock, wine.maxOrderQuantity)
      : Math.min(wine.stock, 24);

    return {
      name: wine.name || '',
      year: wine.harvest || '',
      stock: wine.stock || 0,
      maxOrder: maxOrder,
      location: wine.wineryCountry || '',
      price: wine.price,
      imageUrl: wine.imageUrl
    };
  }
}
